import type { UiFile } from "@/pages/FilePage/hooks/useFileData";
import { type FileListItemDto } from "@/pages/FilePage/services/files.service";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import { RootState } from "../types";
import { isObject } from "lodash";

function mapToUiFile(dto: FileListItemDto): UiFile {
  return {
    id: dto.fileId,
    name: dto.fileName,
    size: dto.sizeBytes ?? null,
    type: dto.mimeType ?? null,
    version: dto.currentVersion ?? 1,
    updatedAt: dto.updatedAt ?? new Date(),
    deleted: dto.isDeleted ?? false,
  };
}

export interface StartMultipartResult {
  index: string; // file.name để phân biệt từng file
  uploadId: string;
  key: string;
  parts?: number;
  success: string;
  error?: string;
  fileId?: string; // fileId từ backend (API mới)
}
export interface StartSinglepartResult {
  index: string;
  presignedUrl: string;
  key: string;
  fileId: string;
  bucketName?: string;
  success: string;
  error?: string;
}

export interface FileAttribute {
  FileName: string;
  contentType: string;
  project: string;
}

export interface MultipartAttribute {
  uploadId: string;
  key: string;
  parts?: number;
}

export interface Part {
  url: string;
  partNumber: number;
}

export interface StatusTask {
  id: string;
  status: number;
  startTime: string;
  endTime: string;
  fileId: string;
  baseFileId: string;
  currentStep: number;
  numberStep: number;
  currentStepName: string;
}

export interface UploadFileStatus1 {
  uniqueId: string; // Unique identifier for each file upload instance
  file: File;
  FileAttribute?: FileAttribute;
  multipartAttribute?: MultipartAttribute & { fileId?: string }; // Thêm fileId từ init
  parts?: Part[];
  uploadDate?: dayjs.Dayjs;
  progress: number;
  completedParts?: number;
  status?: string;
  statusTask?: StatusTask;
  baseFileId?: string;
  version?: string; // Version for overwrite files
  uploadType?: 'single' | 'multi';
}

// Presigned URL cho từng part
export interface SignPartResult {
  index: string; // `${key}_${partNumber}` để mapping
  partNumber: number;
  signedUrl: string;
  success: string;
  error?: string;
}

interface FileExistsConfirmation {
  files: File[];
  existingFiles: Array<{
    file: File;
    fileId: number;
    version?: string;
    currentVersion?: number;
  }>;
  project: string;
  meta: {
    parentFolderId?: string;
    parentFolderIdMap?: Record<string, string>;
    companyId: string;
    teamId?: string;
  };
  isVisible: boolean;
}

// NEW: Conflict Queue Item
interface ConflictQueueItem {
  id: string; // Unique ID for this conflict
  files: File[];
  existingFiles: Array<{
    file: File;
    fileId: number;
    version?: string;
    currentVersion?: number;
  }>;
  project: string;
  meta: {
    parentFolderId?: string;
    parentFolderIdMap?: Record<string, string>;
    companyId: string;
    teamId?: string;
    isOrganization?: boolean;
  };
}

interface UploadState {
  files: UiFile[];
  fileStatusList: UploadFileStatus1[]; // Data Jobs Modal
  uploadModalFileStatusList: UploadFileStatus1[]; // Data Upload Modal
  startMultipartResults: StartMultipartResult[];
  startSinglepartResults: StartSinglepartResult[];
  signPartResults: SignPartResult[];
  config: any;
  currentStep: number;
  fileId: number | null;
  lastUploadAt: number;
  convertingFiles: string[];
  taskNotConverted: string[];
  abortedFiles: string[]; // Track aborted files by fileName
  pendingConfirmation: FileExistsConfirmation | null; // For file exists confirmation workflow
  userConfirmationChoice: "yes" | "no" | null;
  isCheckingFiles: boolean;
  errorMessage: string | null; // Thêm state để lưu error message
  conflictPreference: 'overwrite' | 'createNew' | "cancel" | null; // Remember user's choice for file conflicts
  companyUsage?: number;
  companyDocumentCount?: number;
  companyStorageResponse?: any; // Response từ getCompanyStorageUsageSuccess
  companyCapacitySize: number | null; // Capacity size của company storage
  // ✅ NEW: Conflict Queue
  conflictQueue: ConflictQueueItem[]; // Queue of pending conflicts
  activeConflict: ConflictQueueItem | null; // Currently active conflict being shown to user
  isProcessingConflict: boolean; // Flag to prevent race conditions
}

const initialState: UploadState = {
  files: [],
  fileStatusList: [],
  uploadModalFileStatusList: [],
  startMultipartResults: [],
  startSinglepartResults: [],
  signPartResults: [],
  config: null,
  currentStep: 0,
  fileId: null,
  lastUploadAt: Date.now(),
  convertingFiles: [],
  taskNotConverted: [],
  abortedFiles: [],
  pendingConfirmation: null,
  userConfirmationChoice: null,
  isCheckingFiles: false,
  errorMessage: null,
  conflictPreference: null,
  companyUsage: undefined,
  companyDocumentCount: undefined,
  companyStorageResponse: undefined,
  companyCapacitySize: null,
  // ✅ NEW: Conflict Queue
  conflictQueue: [],
  activeConflict: null,
  isProcessingConflict: false,
};

const uploadFileSlice = createSlice({
  name: "uploadFile",
  initialState,
  reducers: {
    // Action mới: thêm file vào fileStatusList khi bắt đầu upload (cho Jobs Modal)
    addFileToStatusList(state, action: PayloadAction<{ file: File; uniqueId?: string }>) {
      const uniqueId = action.payload.uniqueId || `${action.payload.file.name}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      state.fileStatusList.push({
        uniqueId,
        file: action.payload.file,
        uploadDate: dayjs(),
        progress: 2,
        status: "uploading",
        completedParts: 0,
      });
    },

    // Action mới: thêm file vào uploadModalFileStatusList khi bắt đầu upload (cho Upload Modal)
    addFileToUploadModalStatusList(
      state,
      action: PayloadAction<{ file: File; uniqueId?: string; version?: string }>
    ) {
      const uniqueId = action.payload.uniqueId || `${action.payload.file.name}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const exists = state.uploadModalFileStatusList.find(
        (d) => d.uniqueId === uniqueId
      );
      if (!exists) {
        state.uploadModalFileStatusList.push({
          uniqueId,
          file: action.payload.file,
          uploadDate: dayjs(),
          progress: -1,
          completedParts: 0,
          version: action.payload.version,
          status: "uploading",
        });
      }
    },

    // Action mới: cập nhật progress của file (cho Jobs Modal)
    updateFileProgress(
      state,
      action: PayloadAction<{ fileName?: string; uniqueId?: string; progress: number }>
    ) {
      const fileStatus = state.fileStatusList.find(
        (f) => action.payload.uniqueId ? f.uniqueId === action.payload.uniqueId : f.file.name === action.payload.fileName
      );
      if (fileStatus) {
        fileStatus.progress = Math.max(
          0,
          Math.min(action.payload.progress, 100)
        );
      }
    },

    // Action mới: cập nhật progress của file (cho Upload Modal)
    updateUploadModalFileProgress(
      state,
      action: PayloadAction<{ fileName?: string; uniqueId?: string; progress: number }>
    ) {
      const fileStatus = state.uploadModalFileStatusList.find(
        (f) => action.payload.uniqueId ? f.uniqueId === action.payload.uniqueId : f.file.name === action.payload.fileName
      );
      if (fileStatus) {
        fileStatus.progress = Math.max(
          0,
          Math.min(action.payload.progress, 100)
        );
      }
    },

    // ========================================
    // File existence check actions
    // ========================================
    checkFileExistsRequest(
      state,
      action: PayloadAction<{
        files: File[];
        project: string;
        parentFolderId?: string;
        companyId: string;
        teamId?: string;
        isOrganization?: boolean;
        __queueFileName?: string; // For folder upload queue tracking
        uniqueIds?: string[]; // For folder upload queue tracking
      }>
    ) {
      state.isCheckingFiles = true;
      state.userConfirmationChoice = null;
    },
    checkFileExistsSuccess(
      state,
      action: PayloadAction<{
        files: File[];
        project: string;
        existingFiles: { file: File; fileId: number }[];
      }>
    ) {
      state.isCheckingFiles = false;
    },
    checkFileExistsFailure(state, action: PayloadAction<{ message: string }>) {
      state.isCheckingFiles = false;
      state.errorMessage = action.payload.message;
    },

    // ========================================
    // NEW: File conflict confirmation actions
    // ========================================
    // Dispatched by Epic when conflict detected
    // UI component will listen to this
    showFileConflictConfirm(
      state,
      action: PayloadAction<{
        files: File[];
        project: string;
        existingFiles: Array<{
          file: File;
          fileId: number;
          version?: string;
          currentVersion?: number;
        }>;
        meta: {
          parentFolderId?: string;
          parentFolderIdMap?: Record<string, string>;
          companyId: string;
          teamId?: string;
          isOrganization?: boolean;
          __queueFileName?: string; // For folder upload queue tracking
        };
      }>
    ) {
      // Store complete conflict data including metadata for UI
      state.pendingConfirmation = {
        files: action.payload.files,
        existingFiles: action.payload.existingFiles,
        project: action.payload.project,
        meta: action.payload.meta,
        isVisible: true,
      };
    },

    // Dispatched by UI Modal component when user makes a choice
    // Epic will listen to this and continue the flow
    fileConflictConfirmResult(
      state,
      action: PayloadAction<{
        action: 'overwrite' | 'createNew' | 'cancel';
        rememberChoice?: boolean; // NEW: Remember this choice for all files
        meta: {
          files: File[];
          project: string;
          existingFiles: Array<{
            file: File;
            fileId: number;
            version?: string;
          }>;
          parentFolderId?: string;
          parentFolderIdMap?: Record<string, string>;
          companyId: string;
          teamId?: string;
          isOrganization?: boolean;
          __queueFileName?: string; // For folder upload queue tracking
        };
      }>
    ) {
      // Clear the pending confirmation after user choice
      state.pendingConfirmation = null;
      
      // Save preference if user wants to remember choice
      if (action.payload.rememberChoice) {
        state.conflictPreference = action.payload.action;
      }
    },

    // NEW: Clear conflict preference
    clearConflictPreference(state) {
      state.conflictPreference = null;
    },

    // ========================================
    // ✅ NEW: Conflict Queue Actions
    // ========================================
    // Add conflict to queue
    enqueueConflict(
      state,
      action: PayloadAction<{
        files: File[];
        existingFiles: Array<{
          file: File;
          fileId: number;
          version?: string;
          currentVersion?: number;
        }>;
        project: string;
        meta: {
          parentFolderId?: string;
          parentFolderIdMap?: Record<string, string>;
          companyId: string;
          teamId?: string;
          isOrganization?: boolean;
        };
      }>
    ) {
      const id = `conflict_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const conflictItem: ConflictQueueItem = {
        id,
        ...action.payload,
      };
      
      state.conflictQueue.push(conflictItem);
      console.log(`📥 [enqueueConflict] Added conflict ${id} to queue. Queue length: ${state.conflictQueue.length}`);
    },

    // Process next conflict from queue (triggered by epic)
    processNextConflict(state) {
      if (state.isProcessingConflict) {
        console.warn('⚠️ [processNextConflict] Already processing a conflict, skipping');
        return;
      }

      if (state.conflictQueue.length === 0) {
        state.activeConflict = null;
        state.isProcessingConflict = false;
        return;
      }

      // Dequeue and set as active
      const nextConflict = state.conflictQueue.shift()!;
      state.activeConflict = nextConflict;
      state.isProcessingConflict = true;
      
      // Set pendingConfirmation for UI to show modal
      state.pendingConfirmation = {
        files: nextConflict.files,
        existingFiles: nextConflict.existingFiles,
        project: nextConflict.project,
        meta: nextConflict.meta,
        isVisible: true,
      };
      
    },

    // Mark current conflict as resolved
    resolveCurrentConflict(state) {
      if (state.activeConflict) {
        console.log(`✅ [resolveCurrentConflict] Resolved conflict ${state.activeConflict.id}`);
      }
      state.activeConflict = null;
      state.isProcessingConflict = false;
      state.pendingConfirmation = null;
    },

    // Clear entire conflict queue (e.g., on unmount or cancel all)
    clearConflictQueue(state) {
      console.log(`🗑️ [clearConflictQueue] Clearing ${state.conflictQueue.length} conflicts from queue`);
      state.conflictQueue = [];
      state.activeConflict = null;
      state.isProcessingConflict = false;
      state.pendingConfirmation = null;
    },

    // Action để clear error message
    clearErrorMessage(state) {
      state.errorMessage = null;
    },

    uploadFilesRequest(
      state,
      action: PayloadAction<{
        files: File[];
        project: string;
        fileVersionMap?: Record<string, string | undefined>;
        fileIdMap?: Record<string, number | undefined>;
        parentFolderId?: string;
        parentFolderIdMap?: Record<string, string>;
        companyId: string;
        teamId?: string;
        isOrganization?: boolean;
        __queueFileName?: string; // For folder upload queue tracking
        uniqueIds?: string[]; // For folder upload queue tracking
      }>
    ) {},
    uploadFileResult(
      state,
      action: PayloadAction<{ file: File; result: any }>
    ) {},

      startSinglepartRequest: (
      state,
      action: PayloadAction<{
        file: File;
        project: string;
        uploadIndex: string;
        version?: string;
        existingFileId?: number;
        companyId?: string;
        uniqueId?: string;
      }>
    ) => {
      // Mark file as using single-part upload
      const fileStatus = state.fileStatusList.find(
        (f) => action.payload.uniqueId ? f.uniqueId === action.payload.uniqueId : f.file.name === action.payload.file.name
      );
      // if (fileStatus) {
      //   fileStatus.uploadType = 'single';
      // }
      
      const uploadModalFileStatus = state.uploadModalFileStatusList.find(
        (f) => action.payload.uniqueId ? f.uniqueId === action.payload.uniqueId : f.file.name === action.payload.file.name
      );
      // if (uploadModalFileStatus) {
      //   uploadModalFileStatus.uploadType = 'single';
      // }
    },

        startSinglepartSuccess: (
      state,
      action: PayloadAction<{
        presignedUrl: string;
        key: string;
        fileId: string;
        bucketName?: string;
        file: File;
        uploadIndex?: string;
      }>
    ) => {
      state.startSinglepartResults.push({
        index: action.payload.uploadIndex || action.payload.file.name,
        presignedUrl: action.payload.presignedUrl,
        key: action.payload.key,
        fileId: action.payload.fileId,
        success: "success",
      });
    },

        startSinglepartFailure: (
      state,
      action: PayloadAction<{ file: File; message: any }>
    ) => {
      state.errorMessage = action.payload.message?.Message || "Single-part upload initialization failed";
    },

    // Khởi tạo multipart upload
    startMultipartRequest: (
      state,
      action: PayloadAction<{
        file: File;
        project: string;
        uploadIndex: string;
        version?: string;
        existingFileId?: number;
        companyId?: string;
        uniqueId?: string;
      }>
    ) => {},
    startMultipartSuccess: (
      state,
      action: PayloadAction<{
        uploadId: string;
        key: string;
        file: File;
        uploadIndex?: string;
        fileId?: string;
      }>
    ) => {
      state.startMultipartResults.push({
        index: action.payload.uploadIndex || action.payload.file.name,
        uploadId: action.payload.uploadId,
        key: action.payload.key,
        success: "success",
        fileId: action.payload.fileId, // Lưu fileId từ backend
      });
    },
    startMultipartFailure: (
      state,
      action: PayloadAction<{ file: File; message: any }>
    ) => {
      state.errorMessage = action.payload.message.Message;
    },

    // Action mới: cập nhật FileAttribute
    updateFileAttribute(
      state,
      action: PayloadAction<{ fileName: string; fileAttribute: FileAttribute }>
    ) {
      // Cập nhật cho Jobs Modal fileStatusList
      const fileStatus = state.fileStatusList.find(
        (f) => f.file.name === action.payload.fileName
      );
      if (fileStatus) {
        fileStatus.FileAttribute = action.payload.fileAttribute;
      }

      // Cập nhật cho Upload Modal fileStatusList
      const uploadModalFileStatus = state.uploadModalFileStatusList.find(
        (f) => f.file.name === action.payload.fileName
      );
      if (uploadModalFileStatus) {
        uploadModalFileStatus.FileAttribute = action.payload.fileAttribute;
      }
    },

    // Action mới: cập nhật MultipartAttribute sau khi tính toán partNumbers
    updateMultipartAttribute(
      state,
      action: PayloadAction<{
        fileName?: string;
        uniqueId?: string;
        key: string;
        uploadId: string;
        totalParts: number;
        fileId?: string;
      }>
    ) {
      // Cập nhật cho Jobs Modal fileStatusList
      const fileStatus = state.fileStatusList.find(
        (f) => action.payload.uniqueId ? f.uniqueId === action.payload.uniqueId : f.file.name === action.payload.fileName
      );
      if (fileStatus) {
        fileStatus.multipartAttribute = {
          uploadId: action.payload.uploadId,
          key: action.payload.key,
          parts: action.payload.totalParts,
          fileId: action.payload.fileId,
        };
      }

      // Cập nhật cho Upload Modal fileStatusList
      const uploadModalFileStatus = state.uploadModalFileStatusList.find(
        (f) => action.payload.uniqueId ? f.uniqueId === action.payload.uniqueId : f.file.name === action.payload.fileName
      );
      if (uploadModalFileStatus) {
        uploadModalFileStatus.multipartAttribute = {
          uploadId: action.payload.uploadId,
          key: action.payload.key,
          parts: action.payload.totalParts,
          fileId: action.payload.fileId,
        };
      }
    },

    getConfig: (state, action) => {},
    setConfig: (state, action) => {
      state.config = action.payload;
    },

    // Ký từng part
    signPartRequest: (state, action) => {},
    signPartSuccess: (
      state,
      action: PayloadAction<{ url: string; partNumber: number; index: string }>
    ) => {
      state.signPartResults.push({
        index: action.payload.index, // file.name để phân biệt từng file
        signedUrl: action.payload.url,
        success: "success",
        partNumber: action.payload.partNumber,
      });
    },
    signPartFailure: (state, action: PayloadAction<{ message: string }>) => {
      state.errorMessage = action.payload.message;
    },

    // Action mới: thêm part vào fileStatusList
    addPartToFile(
      state,
      action: PayloadAction<{ fileName: string; part: Part }>
    ) {
      const fileStatus = state.fileStatusList.find(
        (f) => f.file.name === action.payload.fileName
      );
      if (fileStatus) {
        if (!fileStatus.parts) {
          fileStatus.parts = [];
        }
        fileStatus.parts.push(action.payload.part);
      }
    },

    // Upload từng part
    uploadPartRequest: (state, action) => {},
    uploadPartSuccess: (
      state,
      action: PayloadAction<{ fileName?: string; uniqueId?: string; partNumber?: number }>
    ) => {
      // Cập nhật cho Jobs Modal fileStatusList
      const fileStatus = state.fileStatusList.find(
        (f) => action.payload.uniqueId ? f.uniqueId === action.payload.uniqueId : f.file.name === action.payload.fileName
      );
      if (fileStatus && fileStatus.multipartAttribute) {
        const currentCompletedParts = fileStatus.completedParts || 0;
        const totalParts = fileStatus.multipartAttribute.parts || 0;

        // Đảm bảo completedParts không vượt quá totalParts
        if (currentCompletedParts < totalParts) {
          fileStatus.completedParts = currentCompletedParts + 1;

          // Cập nhật progress, đảm bảo không vượt quá 100%
          const calculatedProgress = Math.round(
            (fileStatus.completedParts / totalParts) * 100
          );
          fileStatus.progress = Math.min(calculatedProgress, 100);

        } else {
          console.warn(
            `[Jobs Modal] [${action.payload.fileName}] Attempted to increment completedParts beyond totalParts. Current: ${currentCompletedParts}, Total: ${totalParts}`
          );
        }
      }

      // Cập nhật cho Upload Modal fileStatusList
      const uploadModalFileStatus = state.uploadModalFileStatusList.find(
        (f) => action.payload.uniqueId ? f.uniqueId === action.payload.uniqueId : f.file.name === action.payload.fileName
      );
      if (uploadModalFileStatus && uploadModalFileStatus.multipartAttribute) {
        const currentCompletedParts = uploadModalFileStatus.completedParts || 0;
        const totalParts = uploadModalFileStatus.multipartAttribute.parts || 0;

        // Đảm bảo completedParts không vượt quá totalParts
        if (currentCompletedParts < totalParts) {
          uploadModalFileStatus.completedParts = currentCompletedParts + 1;

          // Cập nhật progress, đảm bảo không vượt quá 100%
          const calculatedProgress = Math.round(
            (uploadModalFileStatus.completedParts / totalParts) * 100
          );
          uploadModalFileStatus.progress = Math.min(calculatedProgress, 100);

        } else {
          console.warn(
            `[Upload Modal] [${action.payload.fileName}] Attempted to increment completedParts beyond totalParts. Current: ${currentCompletedParts}, Total: ${totalParts}`
          );
        }
      }
    },
    uploadPartFailure: (state, action: PayloadAction<{ message: string }>) => {
      state.errorMessage = action.payload.message;
    },

    // Complete multipart upload actions
    completeMultipartRequest: (
      state,
      action: PayloadAction<{
        key: string;
        uploadId: string;
        parts: { PartNumber: number; ETag: string }[];
      }>
    ) => {},
    completeMultipartSuccess: (
      state,
      action: PayloadAction<{
        key: string;
        uploadId: string;
        result: any;
      }>
    ) => {},
    completeMultipartFailure: (
      state,
      action: PayloadAction<{
        key: string;
        uploadId: string;
        message: string;
      }>
    ) => {
      state.errorMessage = action.payload.message;
    },

    // Abort multipart upload actions
    abortMultipartRequest: (
      state,
      action: PayloadAction<{
        key: string;
        uploadId: string;
        fileId?: string;
        uniqueId?: string;
      }>
    ) => {
      // Mark file as being aborted
      const fileStatus = state.fileStatusList.find(
        (f) =>
          f.multipartAttribute?.key === action.payload.key &&
          f.multipartAttribute?.uploadId === action.payload.uploadId
      );
      if (fileStatus) {
        fileStatus.status = "aborting";
        // Add to aborted files array to stop future operations
        if (!state.abortedFiles.includes(fileStatus.file.name)) {
          state.abortedFiles.push(fileStatus.file.name);
        }
      }
    },
    // NEW: Batch abort request
    abortMultipartBatchRequest: (
      state,
      action: PayloadAction<{
        uploads: Array<{
          key: string;
          uploadId: string;
          fileId?: string;
        }>;
      }>
    ) => {
      // Mark all files as being aborted
      action.payload.uploads.forEach(upload => {
        const fileStatus = state.fileStatusList.find(
          (f) =>
            f.multipartAttribute?.key === upload.key &&
            f.multipartAttribute?.uploadId === upload.uploadId
        );
        if (fileStatus) {
          fileStatus.status = "aborting";
          if (!state.abortedFiles.includes(fileStatus.file.name)) {
            state.abortedFiles.push(fileStatus.file.name);
          }
        }
      });
    },
    abortMultipartSuccess: (
      state,
      action: PayloadAction<{
        key: string;
        uploadId: string;
        result: any;
      }>
    ) => {
      // Mark file as failed instead of removing it
      const fileStatus = state.fileStatusList.find(
        (f) =>
          f.multipartAttribute?.key === action.payload.key &&
          f.multipartAttribute?.uploadId === action.payload.uploadId
      );

      if (fileStatus) {
        // Set status to failed when cancelled
        fileStatus.status = "failed";

        // Remove from converting files if it exists
        if (fileStatus.baseFileId) {
          state.convertingFiles = state.convertingFiles.filter(
            (id) => id !== fileStatus.baseFileId
          );
          state.taskNotConverted = state.taskNotConverted.filter(
            (id) => id !== fileStatus.baseFileId
          );
        }

        // Remove from aborted files list since it's been successfully processed
        state.abortedFiles = state.abortedFiles.filter(
          (name) => name !== fileStatus.file.name
        );
      }

      // Cập nhật cho Upload Modal fileStatusList
      const uploadModalFileStatus = state.uploadModalFileStatusList.find(
        (f) =>
          f.multipartAttribute?.key === action.payload.key &&
          f.multipartAttribute?.uploadId === action.payload.uploadId
      );

      if (uploadModalFileStatus) {
        uploadModalFileStatus.status = "failed";

        if (uploadModalFileStatus.baseFileId) {
          state.convertingFiles = state.convertingFiles.filter(
            (id) => id !== uploadModalFileStatus.baseFileId
          );
          state.taskNotConverted = state.taskNotConverted.filter(
            (id) => id !== uploadModalFileStatus.baseFileId
          );
        }

        state.abortedFiles = state.abortedFiles.filter(
          (name) => name !== uploadModalFileStatus.file.name
        );
      }
    },
    // NEW: Batch abort success
    abortMultipartBatchSuccess: (
      state,
      action: PayloadAction<{
        uploads: Array<{
          key: string;
          uploadId: string;
        }>;
        result: any;
      }>
    ) => {
      action.payload.uploads.forEach(upload => {
        const fileStatus = state.fileStatusList.find(
          (f) =>
            f.multipartAttribute?.key === upload.key &&
            f.multipartAttribute?.uploadId === upload.uploadId
        );

        if (fileStatus) {
          fileStatus.status = "failed";

          if (fileStatus.baseFileId) {
            state.convertingFiles = state.convertingFiles.filter(
              (id) => id !== fileStatus.baseFileId
            );
            state.taskNotConverted = state.taskNotConverted.filter(
              (id) => id !== fileStatus.baseFileId
            );
          }

          state.abortedFiles = state.abortedFiles.filter(
            (name) => name !== fileStatus.file.name
          );
        }

        // Cập nhật cho Upload Modal fileStatusList
        const uploadModalFileStatus = state.uploadModalFileStatusList.find(
          (f) =>
            f.multipartAttribute?.key === upload.key &&
            f.multipartAttribute?.uploadId === upload.uploadId
        );

        if (uploadModalFileStatus) {
          uploadModalFileStatus.status = "failed";

          if (uploadModalFileStatus.baseFileId) {
            state.convertingFiles = state.convertingFiles.filter(
              (id) => id !== uploadModalFileStatus.baseFileId
            );
            state.taskNotConverted = state.taskNotConverted.filter(
              (id) => id !== uploadModalFileStatus.baseFileId
            );
          }

          state.abortedFiles = state.abortedFiles.filter(
            (name) => name !== uploadModalFileStatus.file.name
          );
        }
      });
    },
    abortMultipartFailure: (
      state,
      action: PayloadAction<{
        key: string;
        uploadId: string;
        message: string;
      }>
    ) => {
      state.errorMessage = action.payload.message;
      // Reset file status on abort failure
      const fileStatus = state.fileStatusList.find(
        (f) =>
          f.multipartAttribute?.key === action.payload.key &&
          f.multipartAttribute?.uploadId === action.payload.uploadId
      );
      if (fileStatus) {
        fileStatus.status = "error";
      }
    },
    // NEW: Batch abort failure
    abortMultipartBatchFailure: (
      state,
      action: PayloadAction<{
        message: string;
      }>
    ) => {
      state.errorMessage = action.payload.message;
    },

    // NEW: Action để bắt đầu luồng tuần tự complete upload
    completeUploadSequence: (
      state,
      action: PayloadAction<{
        key: string;
        uploadId: string | null;
        fileId?: string;
        parts:
          | { PartNumber: number; ETag: string }[]
          | { partNumber: number; etag: string }[];
        bucket?: string;
        file: File;
        project: string;
        companyId: string;
        teamId?: string;
        isOrganization?: boolean;
        uniqueId?: string;
        isSinglePart?: boolean;
      }>
    ) => {
      const uniqueId = (action.payload.file as any).__uniqueId;
      // Cập nhật cho Jobs Modal fileStatusList
      const fileStatus = state.fileStatusList.find(
        (f) => uniqueId ? f.uniqueId === uniqueId : f.file.name === action.payload.file.name
      );
      if (fileStatus) {
        fileStatus.status = "completing";
      }

      // Cập nhật cho Upload Modal fileStatusList
      const uploadModalFileStatus = state.uploadModalFileStatusList.find(
        (f) => uniqueId ? f.uniqueId === uniqueId : f.file.name === action.payload.file.name
      );
      if (uploadModalFileStatus) {
        uploadModalFileStatus.status = "completing";
      }
    },

    // NEW: Action khi hoàn thành thành công toàn bộ luồng
    completeUploadSequenceSuccess: (
      state,
      action: PayloadAction<{
        file: File;
        fileId: number;
        versionResult: any;
        isOrganization?: boolean;
        __queueFileName?: string; // For folder upload queue tracking
      }>
    ) => {
      const { fileId } = action.payload;
      const uniqueId = (action.payload.file as any).__uniqueId;

      // Cập nhật cho Jobs Modal fileStatusList
      const fileStatus = state.fileStatusList.find(
        (f) => uniqueId ? f.uniqueId === uniqueId : f.file.name === action.payload.file.name
      );
      if (fileStatus) {
        fileStatus.status = "completed";
        fileStatus.progress = 100;
        fileStatus.baseFileId = String(fileId);
      }

      // Cập nhật cho Upload Modal fileStatusList
      const uploadModalFileStatus = state.uploadModalFileStatusList.find(
        (f) => uniqueId ? f.uniqueId === uniqueId : f.file.name === action.payload.file.name
      );
      if (uploadModalFileStatus) {
        uploadModalFileStatus.status = "completed";
        uploadModalFileStatus.progress = 100;
        uploadModalFileStatus.baseFileId = String(fileId);
      }

    },

    // NEW: Action khi có lỗi trong luồng tuần tự
    completeUploadSequenceFailure: (
      state,
      action: PayloadAction<{
        fileName: string;
        uniqueId?: string;
        error?: any;
        message: string;
        file?: File; // Add file object for queue processing
      }>
    ) => {
      state.errorMessage = action.payload.message;
      const { error } = action.payload;
      // Cập nhật cho Jobs Modal fileStatusList
      const fileStatus = state.fileStatusList.find(
        (f) => action.payload.uniqueId ? f.uniqueId === action.payload.uniqueId : f.file.name === action.payload.fileName
      );
      if (fileStatus) {
        fileStatus.status = "error";
      }

      // Cập nhật cho Upload Modal fileStatusList
      const uploadModalFileStatus = state.uploadModalFileStatusList.find(
        (f) => action.payload.uniqueId ? f.uniqueId === action.payload.uniqueId : f.file.name === action.payload.fileName
      );
      if (uploadModalFileStatus) {
        if (error?.ErrorCode === 22) {
          uploadModalFileStatus.status = "over_limit";
        } else {
          uploadModalFileStatus.status = "error";
        }
      }
    },

    setCurrentStep(state, action: PayloadAction<number>) {
      state.currentStep = action.payload;
    },
    // User confirmation choice actions
    setUserConfirmationChoice(
      state,
      action: PayloadAction<"yes" | "no" | null>
    ) {
      state.userConfirmationChoice = action.payload;
    },

    // Reset confirmation choice
    resetUserConfirmationChoice(state) {
      state.userConfirmationChoice = null;
    },
    setFiles: (state, action) => {
      const uiFiles = action.payload.files.map((dto: FileListItemDto) =>
        mapToUiFile(dto)
      );
      state.files = uiFiles;
    },
    setFileId: (state, action: PayloadAction<number>) => {
      state.fileId = action.payload;
    },
    startConversion: (state, action: PayloadAction<string>) => {
      if (!state.convertingFiles.includes(action.payload)) {
        state.convertingFiles.push(action.payload);
      }
    },
    completeConversion: (state, action: PayloadAction<string>) => {
      state.convertingFiles = state.convertingFiles.filter(
        (id) => id !== action.payload
      );
    },
    clearAllConversions: (state) => {
      state.convertingFiles = [];
    },
    // Action mới: Cancel pending files (mark as cancelled, không xóa khỏi danh sách)
    cancelPendingFiles: (
      state,
      action: PayloadAction<{ fileNames?: string[]; uniqueIds?: string[] }>
    ) => {
      const { fileNames = [] } = action.payload;
      
      // Update fileStatusList (Jobs Modal) - mark as cancelled
      state.fileStatusList = state.fileStatusList.map((f) =>
        fileNames.includes(f.file.name)
          ? { ...f, status: "cancelled", progress: -2 }
          : f
      );
      
      // Update uploadModalFileStatusList (Upload Modal) - mark as cancelled
      state.uploadModalFileStatusList = state.uploadModalFileStatusList.map((f) =>
        fileNames.includes(f.file.name)
          ? { ...f, status: "cancelled", progress: -2 }
          : f
      );
      
      // Add to abortedFiles to prevent future processing
      // fileNames.forEach((fileName) => {
      //   if (!state.abortedFiles.includes(fileName)) {
      //     state.abortedFiles.push(fileName);
      //   }
      // });
    },
    addRecordToStatusList(
      state,
      action: PayloadAction<{
        fileId?: string;
        name: string;
        size: number;
        progress?: number;
      }>
    ) {
      const { fileId, name, size, progress } = action.payload;

      if (!fileId) return;

      state.fileStatusList.push({
        uniqueId: `${name}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        file: { name, size } as File,
        baseFileId: fileId,
        uploadDate: dayjs(),
        progress: progress ?? -1,
        completedParts: 0,
      });
    },

    getTaskByBaseFileId: (
      state,
      action: PayloadAction<{ baseFileId: string | string[] }>
    ) => {
      const baseFileIds = Array.isArray(action.payload.baseFileId)
        ? action.payload.baseFileId
        : [action.payload.baseFileId];

      baseFileIds.forEach((id) => {
        if (!state.convertingFiles.includes(id)) {
          state.convertingFiles.push(id);
        }
      });
    },

    updateTaskStatus: (
      state,
      action: PayloadAction<{ baseFileId: string; task: StatusTask }>
    ) => {
      const { baseFileId, task } = action.payload;

      // --- Cập nhật cho Jobs Modal fileStatusList ---
      const matchedRecords = state.fileStatusList.filter(
        (f) =>
          f.baseFileId === baseFileId || f.statusTask?.baseFileId === baseFileId
      );

      if (matchedRecords.length > 0) {
        // Cập nhật tất cả record đã match
        matchedRecords.forEach((record) => {
          record.statusTask = task;
          record.baseFileId = baseFileId;
        });
      } else {
        // Nếu không có record nào match, thêm mới
        state.fileStatusList.push({
          uniqueId: `${baseFileId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          file: {} as File,
          progress: 0,
          baseFileId,
          statusTask: task,
        });
      }

      // Cập nhật cho Upload Modal fileStatusList
      const uploadModalFileStatus = state.uploadModalFileStatusList.find(
        (f) =>
          f.baseFileId === baseFileId || f.statusTask?.baseFileId === baseFileId
      );

      if (uploadModalFileStatus) {
        uploadModalFileStatus.statusTask = task;
        uploadModalFileStatus.baseFileId = baseFileId;
      }
    },

    stopPollingTask: (state, action: PayloadAction<{ baseFileId: string }>) => {
      const { baseFileId } = action.payload;
      state.convertingFiles = state.convertingFiles.filter(
        (id) => id !== baseFileId
      );
    },
    taskNotConverted: (
      state,
      action: PayloadAction<{ baseFileId: string }>
    ) => {
      // console.log("[taskNotConverted]", action.payload.baseFileId);
      if (!state.taskNotConverted.includes(action.payload.baseFileId)) {
        state.taskNotConverted.push(action.payload.baseFileId);
      }
    },
    clearTaskNotConverted: (state, action: PayloadAction<string>) => {
      state.taskNotConverted = state.taskNotConverted.filter(
        (id) => id !== action.payload
      );
    },
    setLastUploadAt(state, action: PayloadAction<number>) {
      state.lastUploadAt = action.payload;
    },
    removePendingStatuses(state) {
      state.fileStatusList = state.fileStatusList.filter(
        (file) => file.progress !== -1 && file.progress !== -2
      );
    },
    clearFileStatusList(state) {
      state.fileStatusList = [];
      state.userConfirmationChoice = null;
      state.isCheckingFiles = false;
    },
    clearUploadModalFileStatusList(state) {
      state.uploadModalFileStatusList = [];
    },
    removeExistingFileRecords(
      state,
      action: PayloadAction<{ fileName: string }>
    ) {
      state.fileStatusList = state.fileStatusList.filter(
        (fileStatus) => fileStatus.file.name !== action.payload.fileName
      );
    },
    clearMultipartResults(state, action: PayloadAction<{ fileName: string }>) {
      // Clear old multipart results for this specific file
      state.startMultipartResults = state.startMultipartResults.filter(
        (result) =>
          result.index !== action.payload.fileName &&
          !result.index.startsWith(action.payload.fileName + "_")
      );
      state.signPartResults = state.signPartResults.filter(
        (result) => !result.index.includes(action.payload.fileName)
      );
    },

    getCompanyStorageUsageRequest(
      state,
      _action: PayloadAction<{ companyId: string; labelId: string }>
    ) {
      // Có thể set flag hoặc log nếu cần
      state.errorMessage = null;
      state.companyStorageResponse = null;
    },
    getCompanyStorageUsageSuccess(state, action: PayloadAction<any>) {
      state.companyUsage = action.payload.usage;
      state.companyDocumentCount = action.payload.documentCount;
      state.companyStorageResponse = action.payload;
      state.companyCapacitySize = action.payload.capacitySize ?? null;
    },
    getCompanyStorageUsageFailure(state, action: PayloadAction<string>) {
      state.errorMessage = action.payload;
      state.companyStorageResponse = null;
    },
    updateCompanyStorageSize(state, action: PayloadAction<number>) {
        if (!state.companyStorageResponse) return;

      const currentSize = Number(state.companyStorageResponse.storageSize ?? 0);
      const delta = Number(action.payload ?? 0);
      state.companyStorageResponse.storageSize = currentSize + delta;
    },
    resetUploadFileState: (state) => {
      state.uploadModalFileStatusList = [];
      state.currentStep = 0;
    }
  },
});


export const {
  setFiles,
  addFileToStatusList,
  addFileToUploadModalStatusList,
  updateFileProgress,
  updateUploadModalFileProgress,
  cancelPendingFiles,
  checkFileExistsRequest,
  checkFileExistsSuccess,
  checkFileExistsFailure,
  uploadFilesRequest,
  uploadFileResult,
  startMultipartRequest,
  startMultipartSuccess,
  startMultipartFailure,
  updateFileAttribute,
  updateMultipartAttribute,
  signPartRequest,
  signPartSuccess,
  signPartFailure,
  addPartToFile,
  uploadPartRequest,
  uploadPartFailure,
  uploadPartSuccess,
  completeMultipartRequest,
  completeMultipartSuccess,
  completeMultipartFailure,
  abortMultipartRequest,
  abortMultipartSuccess,
  abortMultipartFailure,
  abortMultipartBatchRequest,
  abortMultipartBatchSuccess,
  abortMultipartBatchFailure,
  setCurrentStep,
  getConfig,
  setConfig,
  setFileId,
  clearErrorMessage,
  // User confirmation actions
  setUserConfirmationChoice,
  resetUserConfirmationChoice,
  // NEW actions
  completeUploadSequence,
  completeUploadSequenceSuccess,
  completeUploadSequenceFailure,
  getTaskByBaseFileId,
  updateTaskStatus,
  stopPollingTask,
  addRecordToStatusList,
  taskNotConverted,
  clearTaskNotConverted,
  setLastUploadAt,
  clearFileStatusList,
  clearUploadModalFileStatusList,
  clearMultipartResults,
  getCompanyStorageUsageRequest,
  getCompanyStorageUsageSuccess,
  getCompanyStorageUsageFailure,
} = uploadFileSlice.actions;

export const uploadFileActions = uploadFileSlice.actions;
export const uploadFileReducer = uploadFileSlice.reducer;
export const selectConvertingFiles = (state: RootState) =>
  state.uploadFile.convertingFiles;

export const isFileConverting = (state: RootState, fileId: string) =>
  state.uploadFile.convertingFiles.includes(fileId);

export const selectJobsFileStatusList = (state: RootState) =>
  state.uploadFile.fileStatusList;

export const selectUploadModalFileStatusList = (state: RootState) =>
  state.uploadFile.uploadModalFileStatusList;
